import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { login } from "../../store/features/authSlice";
import { Stack, Text, Button, Alert, Loader, Textarea, TextInput, SegmentedControl, Group, Modal, Checkbox, Divider } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle } from "@tabler/icons-react";
import { LegalContractRef } from "common";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useBreakpoints } from "../../utils/useBreakpoints";
import UserProfileService from "../../services/UserProfileService";
import { TOKEN_EXP } from "../../pages/LoginPage/LoginPage";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

const appConfig = require("../../app_config.json");

const TEMPLATE_REGEX = /(\[[^\]]+\]\{\{[^}]+\}\})/g;
const FIELD_REGEX = /^\[([^\]]+)\]\{\{([^}]+)\}\}$/;

const PrivacyConfig = () => {
    const user = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const contracts: LegalContractRef[] = (user.attrs?.legal?.contracts || []).filter((c: any) => c.uuid && c.mode);
    const lastContract = contracts.length > 0 ? contracts[contracts.length - 1] : null;

    const [body, setBody] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [mode, setMode] = useState<"manual" | "template">("manual");
    const [editorBody, setEditorBody] = useState("");
    const [templateText, setTemplateText] = useState("");
    const [templateTextEn, setTemplateTextEn] = useState("");
    const [templateFields, setTemplateFields] = useState<Record<string, string>>({});
    const [accepted, setAccepted] = useState(false);
    const [opened, { open, close }] = useDisclosure(false);
    const { isMobile } = useBreakpoints();

    useEffect(() => {
        if (!lastContract) return;
        setLoading(true);
        axios.post(
            `${appConfig.baseUrl}${appConfig.stage}/manage-legal-contract`,
            { action: "get", uuid: lastContract.uuid },
            { headers: { Authorization: user.token! } }
        )
        .then(res => setBody(res.data.body))
        .catch(() => setBody(null))
        .finally(() => setLoading(false));
    }, [lastContract?.uuid]);

    useEffect(() => {
        fetch("/privacy-template.txt")
            .then(r => r.ok ? r.text() : "")
            .then(setTemplateText)
            .catch(() => setTemplateText(""));
        fetch("/privacy-template-en.txt")
            .then(r => r.ok ? r.text() : "")
            .then(setTemplateTextEn)
            .catch(() => setTemplateTextEn(""));
    }, []);

    const startEditing = () => {
        setEditorBody(body || "");
        setTemplateFields({});
        setMode("manual");
        setEditing(true);
    };

    const handleModeChange = (newMode: string) => {
        if ((editorBody || Object.values(templateFields).some(v => v)) && !window.confirm("Cambiando modalità perderai le modifiche non salvate. Continuare?")) return;
        setEditorBody("");
        setTemplateFields({});
        setMode(newMode as "manual" | "template");
    };

    const templateSegments = useMemo(() => templateText.split(TEMPLATE_REGEX), [templateText]);
    const templateSegmentsEn = useMemo(() => templateTextEn.split(TEMPLATE_REGEX), [templateTextEn]);

    const getBodyFromTemplate = (): string => {
        return templateText.replace(TEMPLATE_REGEX, (match) => {
            const m = match.match(FIELD_REGEX);
            if (!m) return match;
            return templateFields[m[2]] || match;
        });
    };

    const getBodyFromTemplateEn = (): string => {
        return templateTextEn.replace(TEMPLATE_REGEX, (match) => {
            const m = match.match(FIELD_REGEX);
            if (!m) return match;
            return templateFields[m[2]] || match;
        });
    };

    const getTemplateMissingFields = (): string[] => {
        const fields = templateText.match(TEMPLATE_REGEX) || [];
        return fields.map(f => { const m = f.match(FIELD_REGEX); return m ? m[2] : ""; }).filter(f => f && !templateFields[f]?.trim());
    };

    const handleSaveClick = () => {
        if (mode === "manual" && !editorBody.trim()) {
            notifications.show({ color: "red", title: "Errore", message: "Il testo dell'informativa non può essere vuoto" });
            return;
        }
        if (mode === "manual" && editorBody.trim().split(/\s+/).length > 500) {
            notifications.show({ color: "red", title: "Errore", message: "Il testo non può superare le 500 parole" });
            return;
        }
        if (mode === "template") {
            const missing = getTemplateMissingFields();
            if (missing.length > 0) {
                notifications.show({ color: "red", title: "Campi mancanti", message: `Compila: ${missing.join(", ")}` });
                return;
            }
        }
        setAccepted(false);
        open();
    };

    const getFinalBody = (): string => {
        if (mode === "template") {
            return getBodyFromTemplate() + "\n\n--- ENGLISH VERSION ---\n\n" + getBodyFromTemplateEn();
        }
        return editorBody;
    };

    const handleConfirmSave = async () => {
        try {
            setSaving(true);
            const uuid = uuidv4();
            const finalBody = getFinalBody();
            await axios.post(
                `${appConfig.baseUrl}${appConfig.stage}/manage-legal-contract`,
                { action: "save", uuid, mode, body: finalBody },
                { headers: { Authorization: user.token! } }
            );

            const up = await UserProfileService.getUserProfile(user.token!);
            dispatch(login({ ...up, token: user.token!, expiration: TOKEN_EXP }));

            setBody(finalBody);
            setEditing(false);
            close();
            notifications.show({ color: "green", title: "Salvato", message: "Informativa salvata con successo" });
        } catch (error: any) {
            notifications.show({ color: "red", title: "Errore", message: error.message || "Errore durante il salvataggio" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Stack><h1>Configura Informativa Privacy</h1><Loader /></Stack>;

    return (
        <Stack>
            <h1>Configura Informativa Privacy</h1>

            <Alert variant="light" color="blue" icon={<IconInfoCircle />}>
                In qualità di Titolare del trattamento, la Sua struttura ha l'obbligo legale di fornire ai Suoi ospiti un'informativa completa sul trattamento dei loro dati personali prima della raccolta degli stessi. La funzionalità "Self Check-in" prevede che l'ospite inserisca i propri dati tramite un form online. Per garantire la conformità alla normativa vigente, è pertanto obbligatorio inserire di seguito il testo della Sua informativa privacy o un link all’informativa ospitata sul Suo sito. Questo testo sarà automaticamente mostrato all'ospite nel form di check-in. La funzionalità "Self Check-in" <b>non potrà essere attivata</b> finché questo campo non sarà compilato.
            </Alert>

            {!editing && !lastContract && (
                <>
                    <Text c="dimmed">Nessuna informativa configurata</Text>
                    <Button fullWidth onClick={startEditing} mb={180}>Crea informativa</Button>
                </>
            )}

            {!editing && lastContract && body !== null && (
                <>
                    <Text size="sm" c="dimmed">
                        Ultima modifica: {format(lastContract.updatedAt, "dd MMMM yyyy, HH:mm", { locale: it })}
                    </Text>
                    <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{body}</Text>
                    <Button fullWidth onClick={startEditing} mb={180}>Crea nuova versione</Button>
                </>
            )}

            {editing && (
                <>
                    <SegmentedControl
                        fullWidth
                        value={mode}
                        onChange={handleModeChange}
                        data={[
                            { label: "Manuale", value: "manual" },
                            { label: "Assistita", value: "template" },
                        ]}
                    />

                    {mode === "manual" && (
                        <div style={{ position: "relative" }}>
                            <Textarea
                                w="100%"
                                autosize
                                minRows={8}
                                placeholder="Incolla qui il testo della tua informativa privacy in italiano e in inglese"
                                value={editorBody}
                                onChange={(e) => setEditorBody(e.currentTarget.value)}
                            />
                            <Text size="xs" c={editorBody.trim().split(/\s+/).filter(Boolean).length > 500 ? "red" : "dimmed"} style={{ textAlign: "right", marginTop: 4 }}>
                                {editorBody.trim() ? editorBody.trim().split(/\s+/).length : 0} / 500 parole
                            </Text>
                        </div>
                    )}

                    {mode === "template" && (
                        <Stack>
                            {templateSegments.map((segment, i) => {
                                const match = segment.match(FIELD_REGEX);
                                if (match) {
                                    const [, label, field] = match;
                                    return (
                                        <TextInput
                                            key={`field-${field}-${i}`}
                                            w="100%"
                                            label={label}
                                            placeholder={label}
                                            value={templateFields[field] || ""}
                                            onChange={(e) => setTemplateFields(prev => ({ ...prev, [field]: e.target.value }))}
                                        />
                                    );
                                }
                                return segment ? <Text key={`text-${i}`} style={{ whiteSpace: "pre-wrap" }}>{segment}</Text> : null;
                            })}
                        </Stack>
                    )}

                    {mode === "template" && templateTextEn && (
                        <>
                            <Divider label="English Version" labelPosition="center" my="md" />
                            <Stack>
                                {templateSegmentsEn.map((segment, i) => {
                                    const match = segment.match(FIELD_REGEX);
                                    if (match) {
                                        const [, , field] = match;
                                        return <Text key={`en-field-${field}-${i}`} fw={700} style={{ whiteSpace: "pre-wrap" }}>{templateFields[field] || match[1]}</Text>;
                                    }
                                    return segment ? <Text key={`en-text-${i}`} style={{ whiteSpace: "pre-wrap" }}>{segment}</Text> : null;
                                })}
                            </Stack>
                        </>
                    )}

                    {isMobile ? (
                        <Stack>
                            <Button fullWidth onClick={handleSaveClick}>Salva</Button>
                            <Button fullWidth variant="subtle" onClick={() => setEditing(false)}>Annulla</Button>
                        </Stack>
                    ) : (
                        <Group>
                            <Button onClick={handleSaveClick}>Salva</Button>
                            <Button variant="subtle" onClick={() => setEditing(false)}>Annulla</Button>
                        </Group>
                    )}
                </>
            )}

            <Modal opened={opened} onClose={close} title="Conferma salvataggio" centered>
                <Stack>
                    <Text size="sm">
                        Confermo di aver preso visione del testo dell'informativa privacy e di accettarne il contenuto. Dichiaro che il testo è conforme alla normativa vigente sulla protezione dei dati personali.
                    </Text>
                    <Checkbox
                        label="Ho letto e accetto"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.currentTarget.checked)}
                    />
                    <Button fullWidth disabled={!accepted} loading={saving} onClick={handleConfirmSave}>
                        Conferma e salva
                    </Button>
                    <Button fullWidth variant="subtle" onClick={close}>Annulla</Button>
                </Stack>
            </Modal>
        </Stack>
    );
};

export default PrivacyConfig;
